import { useState } from 'react';

import { Check, SendHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { AiClarifyingQuestion } from '../types/ai-report';

interface ReportAiClarificationCardProps {
  questions: AiClarifyingQuestion[];
  /** False once the conversation moved on — the card then shows the questions read-only. */
  isActive: boolean;
  disabled: boolean;
  onSubmit: (answer: string) => void;
}

/** Formats picked/typed answers as one user message the model can match back to its questions. */
const formatAnswers = (
  questions: AiClarifyingQuestion[],
  selected: Record<string, string[]>,
  freeText: Record<string, string>
): string =>
  questions
    .map(q => {
      const answers = [...(selected[q.id] ?? []), freeText[q.id]?.trim()].filter(Boolean);
      return answers.length > 0 ? `${q.question}\n→ ${answers.join(', ')}` : null;
    })
    .filter(Boolean)
    .join('\n\n');

export const ReportAiClarificationCard = ({
  questions,
  isActive,
  disabled,
  onSubmit,
}: ReportAiClarificationCardProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [freeText, setFreeText] = useState<Record<string, string>>({});

  const toggleOption = (question: AiClarifyingQuestion, option: string) => {
    setSelected(prev => {
      const current = prev[question.id] ?? [];
      if (current.includes(option)) {
        return { ...prev, [question.id]: current.filter(o => o !== option) };
      }
      return { ...prev, [question.id]: question.allowMultiple ? [...current, option] : [option] };
    });
  };

  const answer = formatAnswers(questions, selected, freeText);
  const isLocked = !isActive || disabled;

  return (
    <div className="flex flex-col gap-3 rounded-md border bg-background p-3">
      {questions.map((q, idx) => (
        <div key={q.id} className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">
            {idx + 1}. {q.question}
          </p>
          {q.options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {q.options.map(option => {
                const isSelected = selected[q.id]?.includes(option) ?? false;
                return (
                  <button
                    key={option}
                    type="button"
                    disabled={isLocked}
                    onClick={() => toggleOption(q, option)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors disabled:cursor-default',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted disabled:hover:bg-background'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                    {option}
                  </button>
                );
              })}
            </div>
          )}
          {isActive && (
            <Input
              value={freeText[q.id] ?? ''}
              onChange={e => setFreeText(prev => ({ ...prev, [q.id]: e.target.value }))}
              placeholder={t('ai.clarification.otherPlaceholder')}
              className="h-7 text-xs"
              disabled={isLocked}
            />
          )}
        </div>
      ))}

      {isActive && (
        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-7 flex-1 text-xs"
            disabled={isLocked || !answer}
            onClick={() => onSubmit(answer)}
          >
            <SendHorizontal className="mr-1 h-3 w-3" />
            {t('ai.clarification.submit')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={isLocked}
            onClick={() => onSubmit(t('ai.clarification.skipMessage'))}
          >
            {t('ai.clarification.skip')}
          </Button>
        </div>
      )}
    </div>
  );
};
